import React, { memo } from 'react';
import { css, keyframes, useTheme } from '@emotion/react';

type ButtonThemeType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'primaryUnlimited'
  | 'transparent'
  | 'in-visible'
  | 'zero-style';

export type ButtonProps = {
  children: React.ReactNode;
  kind?: ButtonThemeType;
  fluid?: boolean;
  loading?: boolean;
  //so we can pass through any other native button attribute
  [wildcard: string]: any;
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const spinnerStyle = css`
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 0.5em;
  flex-shrink: 0;
`;

/**
 * A simple wrapper for 'button' incorporating theme styling
 */
export const Button = memo((props: ButtonProps) => {
  const theme = useTheme();
  const { children, kind = 'primary', fluid = false, loading = false, ...allOtherProps } = props;
  let button;
  switch (kind) {
    case 'primaryUnlimited':
      button = css`
        && {
          ${theme.mixins.btnPrimaryUnlimited}
        }
      `;
      break;
    case 'secondary':
      button = css`
        && {
          ${theme.mixins.btnSecondary}
        }
      `;
      break;
    case 'tertiary':
      button = css`
        && {
          ${theme.mixins.btnTertiary}
        }
      `;
      break;
    case 'transparent':
      button = css`
        && {
          ${theme.mixins.btnTransparent}
        }
      `;
      break;
    case 'in-visible':
      button = css`
        && {
          ${theme.mixins.btnInvisible}
        }
      `;
      break;
    case 'zero-style':
      button = css`
        && {
          ${theme?.mixins?.btnZeroStyle}
        }
      `;
      break;
    case 'primary':
    default:
      button = css`
        && {
          ${theme?.mixins?.btnPrimary}
        }
      `;
      break;
  }

  const fluidStyles = fluid
    ? css`
        width: 100%;
      `
    : '';

  return (
    <button
      type="button"
      {...allOtherProps}
      css={fluid ? [button, fluidStyles] : button}
      disabled={loading || allOtherProps.disabled}
      aria-busy={loading || undefined}
    >
      {loading && <span css={spinnerStyle} aria-hidden="true" />}
      {children}
    </button>
  );
});
